import { useEffect, useState } from "react";
import { TrendingUp, Sparkles, ShieldCheck, ChevronDown, ChevronRight, Bot, AlertOctagon, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


const DIM_LABELS = {
  motivation: "Seller Motivation",
  life_events: "Life Events",
  receptivity: "CF Receptivity",
  viability: "Deal Viability",
  accessibility: "Owner Access",
  freshness: "Lead Freshness",
};


const TIER_STYLES = {
  critical: { ring: "ring-2 ring-rose-500/60", text: "text-rose-300", bg: "bg-rose-500/15", border: "border-rose-500/40", pulse: "animate-pulse" },
  hot: { ring: "ring-1 ring-rose-500/40", text: "text-rose-300", bg: "bg-rose-500/15", border: "border-rose-500/40", pulse: "" },
  strong: { ring: "ring-1 ring-orange-500/40", text: "text-orange-300", bg: "bg-orange-500/15", border: "border-orange-500/40", pulse: "" },
  qualified: { ring: "ring-1 ring-emerald-500/40", text: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/40", pulse: "" },
  watchlist: { ring: "ring-1 ring-slate-500/40", text: "text-slate-300", bg: "bg-slate-500/15", border: "border-slate-500/40", pulse: "" },
  low: { ring: "ring-1 ring-slate-700/40", text: "text-slate-500", bg: "bg-slate-700/15", border: "border-slate-700/40", pulse: "" },
  compliance_block: { ring: "ring-2 ring-rose-700/60", text: "text-rose-400", bg: "bg-rose-700/20", border: "border-rose-700/50", pulse: "" },
};


export default function ScoreBreakdownPanel({ opp, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recalcing, setRecalcing] = useState(false);

  const recalc = async () => {
    setRecalcing(true);
    try {
      const r = await api.post(`/discovery/opportunities/${opp.oid}/score-v2`);
      toast.success(`Score: ${r.data.cf_readiness_score}/100, ${r.data.cf_score_tier?.label || ""}`);
      onUpdate?.({ ...opp, ...r.data });
      setAiSummary(null);  // invalidate stale AI summary
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recalc failed");
    } finally {
      setRecalcing(false);
    }
  };

  const score = opp?.cf_readiness_score;
  const tier = opp?.cf_score_tier || {};
  const dims = opp?.cf_score_dimensions || {};
  const dimMax = opp?.cf_score_dimension_max || { motivation: 25, life_events: 20, receptivity: 20, viability: 20, accessibility: 10, freshness: 5 };
  const drivers = opp?.cf_score_drivers || [];
  const compound = opp?.cf_score_compound_signals || [];
  const velocity = opp?.cf_score_velocity || 0;
  const confidence = opp?.cf_score_confidence || {};
  const closeProb = opp?.close_probability_pct ?? 0;
  const freshness = opp?.lead_freshness_score ?? 0;
  const complianceBlocked = opp?.cf_compliance_blocked;

  const ts = TIER_STYLES[tier?.tier] || TIER_STYLES.low;

  // Compliance-block state takes over the entire panel
  if (complianceBlocked) {
    return (
      <div className="ae-card p-5 border-rose-700/50" data-testid="score-breakdown-compliance-block">
        <div className="flex items-start gap-3">
          <AlertOctagon className="h-5 w-5 text-rose-400 mt-0.5" />
          <div>
            <div className="font-heading text-lg text-rose-300">Score Unavailable</div>
            <p className="text-xs text-slate-400 mt-1">
              Score withheld pending Fair Housing compliance review. Protected-class proxy variables detected on this opportunity. Notify admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const explainScore = async () => {
    if (aiSummary || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/explain-score`, {});
      setAiSummary(r.data?.explanation || "AI explanation unavailable.");
    } catch (e) {
      setAiSummary("AI explanation unavailable.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`ae-card p-5 ${ts.ring} space-y-4`} data-testid="score-breakdown-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500" data-testid="score-panel-title">
        <span>CF Readiness Score</span>
        <span
          tabIndex={0}
          data-testid="cf-score-info"
          title="Deterministic rules-based score (Creative Finance Readiness). Sums 6 explainable sub-dimensions capped at 100: Motivation 25 + Life Events 20 + Receptivity 20 + Viability 20 + Accessibility 10 + Freshness 5. Plus compound-signal bonuses, velocity tracking, score confidence, and Fair Housing compliance gate. Every point is traceable to a specific driver."
          className="cursor-help text-slate-600 hover:text-emerald-300 transition-colors"
        >
          <Info className="h-3 w-3" />
        </span>
      </div>
      {/* Top: score + tier + close prob */}
      <div className="flex items-baseline justify-between">
        <div>
          <div className="flex items-baseline gap-3">
            <div className={`font-heading text-5xl tracking-tight ${ts.text} ${ts.pulse}`} data-testid="cf-readiness-score">
              {score ?? ", "}
            </div>
            <div className="text-slate-500 text-sm">/100</div>
          </div>
          <div className={`mt-1 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${ts.bg} ${ts.text} ${ts.border}`}
               data-testid="score-tier-label">
            {tier?.label}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right space-y-1">
            <div data-testid="close-probability-badge">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Est. Close Probability</div>
              <div className={`font-heading text-2xl ${closeProb >= 22 ? "text-emerald-300" : closeProb >= 14 ? "text-orange-300" : "text-slate-400"}`}>
                {closeProb}%
              </div>
            </div>
            {confidence?.level && (
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider"
                  data-testid="score-confidence-badge">
                <ShieldCheck className="h-3 w-3" />
                <span className={`${confidence.level === "High" ? "text-emerald-300"
                                : confidence.level === "Medium" ? "text-orange-300" : "text-slate-500"}`}>
                  {confidence.level} confidence ({confidence.datapoints}/{confidence.max})
                </span>
              </div>
            )}
          </div>
          <button onClick={recalc} disabled={recalcing}
                  data-testid="recalc-score-btn" title="Recalculate"
                  className="p-1.5 rounded-sm text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${recalcing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Velocity + freshness rail */}
      <div className="flex items-center gap-4 text-xs">
        {velocity >= 2 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                data-testid="score-accelerating-badge">
            <TrendingUp className="h-3 w-3" /> Score Accelerating ({velocity} signals/30d)
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-slate-500" data-testid="lead-freshness-display">
          Freshness: <span className={freshness >= 4 ? "text-emerald-400" : freshness >= 2 ? "text-orange-400" : "text-slate-400"}>{freshness}/5</span>
          {opp?.lead_signal_date && (
            <span className="text-slate-600">| signal {fmtDateTime(opp.lead_signal_date)}</span>
          )}
        </span>
      </div>

      {/* Dimension bars */}
      <div className="space-y-1.5" data-testid="score-dimension-bars">
        {Object.entries(DIM_LABELS).map(([key, label]) => {
          const val = dims[key] ?? 0;
          const max = dimMax[key] ?? 1;
          const pct = Math.min(100, (val / max) * 100);
          const barColor = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-orange-500" : pct >= 25 ? "bg-slate-500" : "bg-slate-700";
          return (
            <div key={key} className="text-xs" data-testid={`dim-bar-${key}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-slate-300">{label}</span>
                <span className="text-slate-500 font-mono">{val}/{max}</span>
              </div>
              <div className="h-1 bg-white/5 rounded-sm overflow-hidden mt-0.5">
                <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Compound signals */}
      {compound.length > 0 && (
        <div className="space-y-1.5" data-testid="compound-signals-list">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Compound Signals Fired
          </div>
          {compound.map((c, i) => (
            <div key={i} className="text-xs text-slate-200 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-2 py-1">
              <span className="text-emerald-300 font-medium">+{c.bonus}</span> {c.label}
            </div>
          ))}
        </div>
      )}

      {/* Expandable: full signal-by-signal breakdown */}
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-200"
              data-testid="score-breakdown-toggle">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Why this score? ({drivers.length} signals)
      </button>
      {open && (
        <div className="space-y-1" data-testid="score-drivers-list">
          {drivers.map((d, i) => (
            <div key={i} className="text-xs flex items-baseline justify-between border-b border-white/5 py-1">
              <span className="text-slate-300">{d.reason}</span>
              <span className={`font-mono ${d.points > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {d.points > 0 ? "+" : ""}{d.points}
              </span>
            </div>
          ))}
          <div className="pt-2">
            <button onClick={explainScore} disabled={busy || aiSummary}
                    className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1"
                    data-testid="ai-explain-score-btn">
              <Bot className="h-3 w-3" />
              {busy ? "Generating..." : aiSummary ? "AI summary loaded" : "Plain-English summary (AI)"}
            </button>
            {aiSummary && (
              <div className="mt-2 text-xs text-slate-300 bg-white/3 border border-white/8 rounded-sm p-2 leading-relaxed whitespace-pre-line"
                   data-testid="ai-explanation-text">
                {aiSummary}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
